import countlyVue from '../../compat/vue-core';

const BufferedObject = {
  props: {
    initialEditedObject: {
      type: Object,
      default() {
        return {};
      },
    },
    beforeCopyFn: { type: Function },
  },
  data() {
    return {
      editedObject: this.copyOfEdited(),
    };
  },
  watch: {
    initialEditedObject() {
      this.reload();
    },
  },
  methods: {
    reload() {
      this.editedObject = this.copyOfEdited();
      this.reset();
      this.$emit('copy', this.editedObject);
    },
    copyOfEdited() {
      const copied = JSON.parse(JSON.stringify(this.initialEditedObject));
      if (this.beforeCopyFn) {
        return this.beforeCopyFn(copied);
      }

      return copied;
    },
  },
};

const MultiStepForm = {
  mixins: [BufferedObject],
  props: {
    requiresAsyncSubmit: { type: Boolean, default: false, required: false },
    requiresAsyncNext: { type: Boolean, default: false, required: false },
    setStepCallbackFn: { type: Function, default: null, required: false },
  },
  data() {
    return {
      currentStepIndex: 0,
      stepContents: [],
      isMounted: false,
      isSubmissionAllowed: true,
      isSubmitPending: false,
      isNextPending: false,
    };
  },
  computed: {
    lastValidIndex() {
      if (!this.isMounted) {
        return -1;
      }
      let i = 0;
      for (i = 0; i < this.stepContents.length; i += 1) {
        if (this.stepContents[i].isStep && !this.stepContents[i].isValid) {
          return i;
        }
      }
      return i;
    },
    activeContentId() {
      if (this.activeContent) {
        return this.activeContent.tId;
      }
      return null;
    },
    currentScreenMode() {
      if (this.activeContent && this.activeContent.screen) {
        return this.activeContent.screen;
      }
      return 'half';
    },
    currentStepId() {
      return this.activeContentId;
    },
    isCurrentStepValid() {
      if (this.activeContent.isStep) {
        return this.activeContent.isValid;
      }
      return true;
    },
    isLastStep() {
      return this.stepContents.length > 1 && this.currentStepIndex === this.stepContents.length - 1;
    },
    activeContent() {
      if (this.currentStepIndex > this.stepContents.length - 1) {
        return null;
      }
      return this.stepContents[this.currentStepIndex];
    },
    isMultiStep() {
      return this.stepContents.length > 1;
    },
    isValid() {
      if (!this.isMounted) {
        return true;
      }
      return this.stepContents.reduce((item, current) => {
        if (current.isStep) {
          return item && current.isValid;
        }
        return item;
      }, true);
    },
    passedScope() {
      const defaultKeys = ['editedObject', 'currentStepId', 'isSubmissionAllowed', 'submit', 'reset', 'validate'];

      const passed = defaultKeys.reduce((acc, val) => {
        acc[val] = this[val];
        return acc;
      }, {});

      return passed;
    },
  },
  watch: {
    isValid(newValue) {
      this.$nextTick(() => {
        this.isSubmissionAllowed = newValue;
      });
    },
  },
  mounted() {
    this.stepContents = this.$children.filter((child) => child.isContent && child.role === 'default');
    this.isMounted = true;
  },
  methods: {
    preventSubmit() {
      this.isSubmitPending = true;
      this.isSubmissionAllowed = false;
    },
    allowSubmit() {
      this.isSubmitPending = false;
      this.isSubmissionAllowed = true;
    },
    preventNext() {
      this.isNextPending = true;
      this.isSubmissionAllowed = false;
    },
    allowNext() {
      this.isNextPending = false;
      this.isSubmissionAllowed = true;
    },
    setStep(newIndex, originator, internalValidationFailed) {
      const defaultAction = () => {
        if (!internalValidationFailed && newIndex >= 0 && newIndex < this.stepContents.length) {
          if (newIndex !== this.currentStepIndex) {
            const temp = this.currentStepIndex;
            this.currentStepIndex = newIndex;
            this.$emit('step-change', { prev: temp, current: newIndex });
          }
        }
      };
      if (this.setStepCallbackFn) {
        // .resolve() handles non-Promise return values as well
        Promise.resolve(this.setStepCallbackFn(newIndex, this.currentStepIndex, originator))
          .then((value) => {
            if (value) {
              defaultAction();
            }
          })
          .catch(() => { // err
            // TODO: handle error
          });
      } else {
        defaultAction();
      }
    },
    setStepSafe(newIndex, originator) {
      this.beforeLeavingStep(newIndex < this.currentStepIndex ? 'skip' : 'onlyCurrent');
      this.setStep(newIndex, originator, newIndex > this.lastValidIndex);
    },
    prevStep() {
      this.setStep(this.currentStepIndex - 1, 'prev');
    },
    nextStep(force) {
      this.beforeLeavingStep('onlyCurrent');
      if (this.isSubmissionAllowed || force === true) {
        if (this.requiresAsyncNext) {
          this.isNextPending = true;
          const callback = (err) => {
            this.isNextPending = false;
            if (!err) this.setStep(this.currentStepIndex + 1, 'next', !this.isCurrentStepValid);
          };
          this.$emit('next', this.currentStepIndex + 1, force, callback);
        } else {
          this.$emit('next', this.currentStepIndex + 1);
          this.setStep(this.currentStepIndex + 1, 'next', force, !this.isCurrentStepValid);
        }
      }
    },
    reset() {
      // this.callValidators("reset");
      this.setStep(0, 'reset');
      this.$nextTick(() => {
        this.callValidators('reset');
      });
    },
    submit(force) {
      this.beforeLeavingStep();
      if (this.isSubmissionAllowed || force === true) {
        if (this.requiresAsyncSubmit) {
          this.isSubmitPending = true;
          const callback = (err) => {
            this.isSubmitPending = false;
            if (!err && this.doClose) {
              this.doClose();
            }
          };
          this.$emit('submit', JSON.parse(JSON.stringify(this.editedObject)), callback);
        } else {
          this.$emit('submit', JSON.parse(JSON.stringify(this.editedObject)));
          if (this.doClose) {
            this.doClose();
          }
        }
      }
    },
    validate() {
      this.callValidators('touch');
    },
    beforeLeavingStep(touchPolicy) {
      if (touchPolicy === 'onlyCurrent') {
        this.callValidators('touch', true);
      } else if (touchPolicy !== 'skip') {
        this.callValidators('touch');
      }
      this.$emit('before-leaving-step');
    },
    callValidators(command, onlyCurrent) {
      if (onlyCurrent) {
        const target = this.stepContents[this.currentStepIndex];
        if (target && target[command]) {
          target[command]();
        }
      } else {
        this.stepContents.forEach((current) => {
          if (current[command]) {
            current[command]();
          }
        });
      }
    },
  },
};
const BaseStep = countlyVue.mixins.BaseContent.extend({
  data() {
    return {
      isValid: true,
      isStep: true,
    };
  },
});

const BaseFormField = {
  props: {
    bails: {
      type: Boolean,
      default: false,
    },
    rules: {
      required: false,
      default: '',
    },
  },
  methods: {
    ruleStatus(validation) {
      let ruleKeys = [];
      if (typeof this.rules === 'string') {
        ruleKeys = this.rules.split('|');
      } else {
        ruleKeys = Object.keys(this.rules);
      }
      const expectedRuleKeys = ruleKeys.map((rule) => rule.split(':')[0]);
      if (!validation.validated) {
        return expectedRuleKeys.reduce((acc, val) => {
          acc[val] = false;
          return acc;
        }, {});
      }
      const failedRuleKeys = Object.keys(validation.failedRules);
      return expectedRuleKeys.reduce((acc, val) => {
        acc[val] = !failedRuleKeys.includes(val);
        return acc;
      }, {});
    },
  },
};

export default {
  BufferedObject,
  MultiStepForm,
  BaseStep,
  BaseFormField,
};
